import React, { useEffect, useRef, useState } from "react";
import { styled, Typography } from "@mui/material";
import { usePatientCase } from "./usePatientCase";
import { GenderPicker } from "./common/genderPicker";
import { AgePicker } from "./common/agePicker";
import { MobileView } from "../../util/aKaunGeneralUtil";
import { ChiefComplaintPanel } from "./editPatient/ChiefComplaintPanel";
import { DesktopOnly } from "./util/desktopOnly";
import { LimitAgeDialog } from "./common/LimitAgeDialog";
import { RosebudMobileBottomNavigationHeight } from "./common/rosebudButtomNavigationButton";
import { safeScroll } from "./scroll/safeScroll";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { SantanaMobileStepperHeight } from "./common/santanaStepper";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";

//region [[ Styles ]]
const AgeAndGenderView = styled("div")(({ theme }) => ({
  height: "100%",
  ...scrollableStyle,
  [theme.breakpoints.down(MobileView)]: {
    paddingBottom: SantanaMobileStepperHeight,
    background: KahunColorMap.Background,
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: "30px",
  fontWeight: 500,
  color: "#333",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down(MobileView)]: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
    textAlign: "center",
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontSize: "24px",
    // color: KahunColorMap.White,
  },
}));

const Gender = styled(GenderPicker)(({ theme }) => ({
  marginBottom: "3%",
  [theme.breakpoints.down(MobileView)]: {
    marginBottom: 0,
  },
  "& .gender-title": {
    fontSize: "20px",
    fontWeight: 400,
    [theme.breakpoints.down(MobileView)]: {
      textAlign: "center",
      marginBottom: theme.spacing(0.5),
      // color: KahunColorMap.White,
      color: "#333",
    },
  },
}));

const Age = styled(AgePicker)(({ theme }) => ({
  // "& .MuiFormControl-root": { borderRadius: "65px" },
  "& .section-title": {
    fontSize: "20px",
    fontWeight: 400,
    [theme.breakpoints.down(MobileView)]: {
      textAlign: "center",
      marginBottom: theme.spacing(0.5),
      // color: KahunColorMap.White,
      color: "#333",
    },
  },
}));

const AgeGenderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  flexDirection: "row",
  justifyContent: "flex-start",
  [theme.breakpoints.down(MobileView)]: {
    flexDirection: "column",
  },
}));
//endregion [[ Styles ]]

interface PatientDataInputDemographicProps {
  className?: string;
}

export const AgeAndGender = ({
  ...props
}: PatientDataInputDemographicProps) => {
  // Spread out the age field from the patient case

  useEffect(() => {
    (inputRef.current as any).blur();
  }, []);

  const {
    patientCase: { age, gender },
    patientCase,
    handleSetChiefComplaint,
    handleRemoveChiefComplaint,
  } = usePatientCase();

  const [isLimitAgePopupOpen, setIsLimitAgePopupOpen] = useState(false);

  // Keep a ref object to be able to focus into the age text filed
  // after setting the gender
  const inputRef = useRef<HTMLInputElement>();

  // Keep a ref to scroll down when focusing on age
  const containerRef = useRef<HTMLDivElement>();

  // Focus on age after selecting a gender
  const setFocusOnAge = () => {
    if (!age?.low) {
      (inputRef.current as any).focus();
      const offset =
        inputRef.current.getBoundingClientRect?.().bottom +
        RosebudMobileBottomNavigationHeight;

      safeScroll(containerRef, offset, "smooth");
    }
  };

  const onAgeBlur = () => {
    if (patientCase?.age?.low && patientCase?.age?.low < 18) {
      setIsLimitAgePopupOpen(true);
    }
  };

  return (
    <AgeAndGenderView className={props.className} ref={containerRef}>
      <MainTitle color="textSecondary" gutterBottom>
        Get started by telling us about your patient
      </MainTitle>
      <AgeGenderContainer>
        <Gender onGenderChanged={setFocusOnAge} />
        <Age inputRef={inputRef} title={"Age"} onBlur={onAgeBlur} />
        <LimitAgeDialog
          isOpen={isLimitAgePopupOpen}
          onClose={() => setIsLimitAgePopupOpen(false)}
        />
      </AgeGenderContainer>
      <DesktopOnly>
        <ChiefComplaintPanel
          removeChiefComplaint={handleRemoveChiefComplaint}
          setChiefComplaint={handleSetChiefComplaint}
          chiefComplaint={patientCase.main?.[0]} //TODO change when we support multiple
          gender={gender}
        />
      </DesktopOnly>
    </AgeAndGenderView>
  );
};
